import React, { useEffect, useState, useCallback } from 'react';
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    ReferenceLine
} from 'recharts';

const Revenue = () => {
    const [activeFilter, setActiveFilter] = useState('Week');
    const [activeCard, setActiveCard] = useState(null);
    const [open, setOpen] = useState(false);
    const [hoveredBar, setHoveredBar] = useState(null);
    const [hoveredBarRect, setHoveredBarRect] = useState(null);

  const [hoveredValue, setHoveredValue] = useState(null);

    // Memoized custom bar shape for hover effect
     const renderBarShape = useCallback((props) => {
        const { x, y, width, height, index, value } = props;
        const isHovered = hoveredBar === index;
        return (
          <g>
            <rect
              x={x}
              y={y}
              width={width}
              height={height}
              rx={6}
              fill={isHovered ? "#5B4DF7" : "#6D5EF7"}
              opacity={isHovered ? 1 : 0.25}
              style={{ transition: 'all 0.2s' }}
              onMouseEnter={() => {
                setHoveredBar(index);
                setHoveredValue(value);
              }}
              onMouseLeave={() => {
                setHoveredBar(null);
                setHoveredValue(null);
              }}
            />
          </g>
        );
      }, [hoveredBar]);
    // Auth cookie helper
    const getCookie = (name) => {
        const match = document.cookie.match(
            new RegExp("(?:^|; )" + name.replace(/([.$?*|{}()\[\]\\\/\+^])/g, "\\$1") + "=([^;]*)")
        );
        return match ? decodeURIComponent(match[1]) : null;
    };

    // API series state
    const [labels, setLabels] = useState([]);
    const [androidSeries, setAndroidSeries] = useState([]);
    const [iosSeries, setIosSeries] = useState([]);
    const [websiteSeries, setWebsiteSeries] = useState([]);
    const [totalSeries, setTotalSeries] = useState([]);
    const [androidRevenue, setAndroidRevenue] = useState(0);
    const [iosRevenue, setIosRevenue] = useState(0);
    const [websiteRevenue, setWebsiteRevenue] = useState(0);
    const [totalRevenue, setTotalRevenue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Build overview chart data from API series
    const buildOverviewData = () => {
        const out = [];
        for (let i = 0; i < labels.length; i++) {
            const android = Number(androidSeries[i] ?? 0);
            const ios = Number(iosSeries[i] ?? 0);
            const website = Number(websiteSeries[i] ?? 0);
            const total = Number(totalSeries[i] ?? android + ios + website);
            out.push({
                name: labels[i] ?? String(i + 1),
                total,
                android,
                ios,
                website,
            });
        }
        return out;
    };

    // Build single metric chart data
    const buildSingleMetricData = (metric) => {
        const arr = [];
        for (let i = 0; i < labels.length; i++) {
            let value = 0;
            if (metric === "total") value = Number(totalSeries[i] ?? 0);
            else if (metric === "android") value = Number(androidSeries[i] ?? 0);
            else if (metric === "ios") value = Number(iosSeries[i] ?? 0);
            else if (metric === "website") value = Number(websiteSeries[i] ?? 0);
            arr.push({ name: labels[i] ?? String(i + 1), value });
        }
        return arr;
    };

    const chartTitle = {
        total: "Total Revenue Trend",
        android: "Android Revenue Trend",
        ios: "iOS Revenue Trend",
        website: "Website Revenue Trend",
    }[activeCard] || "Revenue Overview";

    const filters = ['Day', 'Week', 'Month', 'Quarter', 'Year'];

    // Fetch analytics when filter changes
    useEffect(() => {
        const fetchAnalytics = async () => {
            const token = getCookie("admin_token");
            if (!token) {
                setError("Not authenticated");
                setLabels([]);
                setAndroidSeries([]);
                setIosSeries([]);
                setWebsiteSeries([]);
                setTotalSeries([]);
                return;
            }
            setLoading(true);
            setError("");
            try {
                const body = new URLSearchParams();
                body.append("range", activeFilter.toLowerCase());
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/admin/revenue/analytics`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/x-www-form-urlencoded",
                        Authorization: `Bearer ${token}`,
                    },
                    body: body.toString(),
                });
                if (!res.ok) throw new Error("Failed to load analytics");
                const json = await res.json();
                const d = json?.data?.[0];
                const series = d?.series || {};
                setLabels(series.labels || []);
                setAndroidSeries(series.android_revenue || []);
                setIosSeries(series.ios_revenue || []);
                setWebsiteSeries(series.website_revenue || []);
                setTotalSeries(series.total_revenue || []);
                setAndroidRevenue(d?.android_revenue || 0);
                setIosRevenue(d?.ios_revenue || 0);
                setWebsiteRevenue(d?.website_revenue || 0);
                setTotalRevenue(d?.total_revenue || 0);
            } catch (e) {
                setError(e.message || "Failed to load analytics");
                setLabels([]);
                setAndroidSeries([]);
                setIosSeries([]);
                setWebsiteSeries([]);
                setTotalSeries([]);
            } finally {
                setLoading(false);
            }
        };
        fetchAnalytics();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeFilter]);

    return (
        <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto overscroll-contain bg-gradient-to-br from-indigo-50 to-purple-50 p-3 pb-6 sm:gap-6 sm:p-4 md:p-6 touch-pan-y [-webkit-overflow-scrolling:touch]">
            <h1 className="shrink-0 text-2xl font-bold text-gray-800 sm:text-3xl">Revenue</h1>

            {error && (
                <p className="shrink-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {error}
                </p>
            )}

            {/* Top Cards */}
            <div className="grid shrink-0 grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4 lg:gap-6">
                <InfoCard
                    title="Total Revenue"
                    value={`₹${totalRevenue.toFixed(2)}`}
                    active={activeCard === 'total'}
                    onClick={() => setActiveCard(activeCard === 'total' ? null : 'total')}
                />
                <InfoCard
                    title="Website Revenue"
                    value={`₹${websiteRevenue.toFixed(2)}`}
                    active={activeCard === 'website'}
                    onClick={() => setActiveCard(activeCard === 'website' ? null : 'website')}
                />
                <InfoCard
                    title="Android Revenue"
                    value={`₹${androidRevenue.toFixed(2)}`}
                    active={activeCard === 'android'}
                    onClick={() => setActiveCard(activeCard === 'android' ? null : 'android')}
                />
                <InfoCard
                    title="iOS Revenue"
                    value={`₹${iosRevenue.toFixed(2)}`}
                    active={activeCard === 'ios'}
                    onClick={() => setActiveCard(activeCard === 'ios' ? null : 'ios')}
                />

            </div>

            <div className="flex shrink-0 flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
                <h2 className="text-lg font-semibold text-gray-800 sm:text-xl">
                    {chartTitle}
                </h2>

                {/* <div className="relative">
                    <button
                        onClick={() => setOpen(!open)}
                        className="bg-white border rounded-lg px-4 py-2 flex items-center gap-2"
                    >
                        {activeFilter}
                        <span>⌄</span>
                    </button>

                    {open && (
                        <div className="absolute mt-2 bg-white rounded-xl shadow w-32 z-10">
                            {filters.map((f) => (
                                <button
                                    key={f}
                                    onClick={() => {
                                        setActiveFilter(f);
                                        setOpen(false);
                                    }}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-100"
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    )}
                </div> */}
            </div>
            <div className="min-w-0 flex-1 rounded-2xl bg-white p-3 shadow-lg sm:p-4 md:p-6">
                {/* HEADER */}
                <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-4">
                    <p className="shrink-0 text-sm font-medium text-gray-500 sm:text-base">
                        Revenue in
                    </p>

                    <div className="relative shrink-0">
                        <button
                            type="button"
                            onClick={() => setOpen(!open)}
                            className="flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm sm:px-4"
                        >
                            {activeFilter}
                            <img src="./chatbar/dark-arrow.svg" alt="" className={`h-4 w-4 opacity-60 ${open ? "-rotate-90" : "rotate-90"}`} />
                        </button>

                        {open && (
                            <div className="absolute left-0 z-20 mt-2 w-32 rounded-xl bg-white shadow-lg">
                                {filters.map((f) => (
                                    <button
                                        key={f}
                                        type="button"
                                        onClick={() => {
                                            setActiveFilter(f);
                                            setOpen(false);
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100"
                                    >
                                        {f}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    </div>
                     {activeCard === null && (
                    <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6">
                        <div className="flex items-center gap-2">
                            <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#1E3A8A" }} />
                            <span className="text-xs text-gray-600 sm:text-sm">Total</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#059669" }} />
                            <span className="text-xs text-gray-600 sm:text-sm">Website</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#4F46E5" }} />
                            <span className="text-xs text-gray-600 sm:text-sm">Android</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: "#7C3AED" }} />
                            <span className="text-xs text-gray-600 sm:text-sm">iOS</span>
                        </div>
                    </div>
                )}

                </div>

                {loading && (
                    <p className="mb-3 text-center text-sm text-gray-500">Loading analytics…</p>
                )}

                <div className="h-[240px] min-w-0 w-full sm:h-[300px] md:h-[370px]">
                    <ResponsiveContainer width="100%" height="100%">
                        {activeCard === null ? (
                            /* 🔥 OVERVIEW MULTI-LINE GRAPH */
                            <LineChart data={buildOverviewData()} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                                <XAxis
                                    dataKey="name"
                                    tick={{ fontSize: 10, fill: "#6b7280" }}
                                    axisLine={false}
                                    tickLine={false}
                                    interval="preserveStartEnd"
                                    minTickGap={8}
                                />
                                <YAxis
                                    tick={{ fontSize: 10, fill: "#6b7280" }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={40}
                                />
                                <Tooltip
                                    contentStyle={{
                                        borderRadius: '8px',
                                        border: '1px solid #e5e7eb',
                                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                        backgroundColor: 'white'
                                    }}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="total"
                                    stroke="#1E3A8A"
                                    strokeWidth={2}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="website"
                                    stroke="#059669"
                                    strokeWidth={2}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="android"
                                    stroke="#4F46E5"
                                    strokeWidth={2}
                                />
                                <Line
                                    type="monotone"
                                    dataKey="ios"
                                    stroke="#7C3AED"
                                    strokeWidth={2}
                                />
                            </LineChart>
                        ) : (
                            /* 🔥 SINGLE BAR GRAPH */
                            <BarChart data={buildSingleMetricData(activeCard)} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                                <XAxis
                                    dataKey="name"
                                    tick={{ fontSize: 10, fill: "#6b7280" }}
                                    axisLine={false}
                                    tickLine={false}
                                    interval="preserveStartEnd"
                                    minTickGap={6}
                                    tickFormatter={(label) => {
                                        if (typeof label === "string" && /^\d{4}-\d{2}-\d{2}$/.test(label)) {
                                            return label.split("-")[2];
                                        }
                                        if (typeof label === "string" && label.length > 6) {
                                            return label.slice(0, 5);
                                        }
                                        return label;
                                    }}
                                />
                                <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} width={40} axisLine={false} tickLine={false} />
                                <Tooltip cursor={false}/>
                                {/* Custom horizontal line from left to hovered bar only */}
                                  {hoveredBar !== null && (
                                           <ReferenceLine
                                             y={hoveredValue}
                                             stroke="#6D5EF7"
                                             strokeDasharray="4 4"
                                             strokeWidth={1}
                                           />
                                         )}
                                <Bar
                                    dataKey="value"
                                    radius={[6, 6, 0, 0]}
                                    barSize={activeFilter === "Day" ? 24 : 20}
                                    maxBarSize={28}
                                    shape={renderBarShape}
                                />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                </div>
            </div>

        </div>
    );
};
function InfoCard ({ title, value, active, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`w-full rounded-xl p-4 text-left shadow-sm transition sm:p-5 sm:py-6
        ${active ? "border-2 border-[#7A5AF8] bg-[#7A5AF8] bg-opacity-20" : "border-2 border-white bg-white hover:bg-gray-50"}
      `}
        >
            <p className="mb-1 text-sm font-semibold text-black sm:mb-2 sm:text-base">{title}</p>
            <p className="aoboshi-one break-all text-lg font-bold text-black sm:text-2xl">{value}</p>
        </button>
    );
}

export default Revenue;